import type {DispatchBoardResponse} from 'src/dispatch/schemas'
import {
    bookingCoversDay,
    type DispatchJobSchedule,
    type DispatchTimelineData,
    type JobAllocation,
    type JobRequirement,
} from 'src/dispatch-timeline/types'
import {formatJobTime, jobTimeToMinutes} from 'src/jobs/job-schedule-format'
import {haversineMiles, type LatLng, toRadians} from 'src/lib/haversine-miles'
import {isRealSite} from 'src/lib/texas-bounds'

export const ON_SITE_MINUTES = 60
export const ON_SITE_ASSUMPTION_LABEL = 'assumes 1 h on site'

export const ROAD_FACTOR = 1.3
export const ESTIMATE_MPH = 45
export const ESTIMATE_METHOD_LABEL = 'approx. straight-line miles × 1.3 at 45 mph'

export type DriveMethod = 'google' | 'estimate'

export type LegTime = {miles: number; minutes: number; method: DriveMethod}

export type RunMapLocation = {name: string; latitude: number | null; longitude: number | null}

/** Per-job board response fields not kept by the timeline adapter. */
export type RunMapJobExtras = {
    locations: RunMapLocation[]
    closerYardId: string | null
    closerYardName: string | null
    closerYardReason?: string | null
}

export function runMapJobExtras(board: DispatchBoardResponse): Record<string, RunMapJobExtras> {
    return Object.fromEntries(
        board.jobs.map((job) => [
            job.id,
            {
                locations: job.locations.map(({name, latitude, longitude}) => ({
                    name,
                    latitude,
                    longitude,
                })),
                closerYardId: job.closerYardId ?? null,
                closerYardName: job.closerYardName ?? null,
                closerYardReason: job.closerYardReason ?? null,
            },
        ]),
    )
}

export type RunMapYard = {
    id: string
    name: string
    latitude: number | null
    longitude: number | null
}

export type RunMapCrewMember = {id: string; name: string}

export type LocationIssue = 'no_location' | 'not_a_site'

export type Staffing = 'staffed' | 'needs_driver'

export type RunMapJob = {
    id: string
    jobCode: string
    label: string
    customer: string
    customerId?: string | null
    yardId: string | null
    /** Minutes after business midnight. */
    startMinutes: number | null
    staffing: Staffing
    crew: RunMapCrewMember[]
    position: LatLng | null
    locationName: string | null
    locationIssue: LocationIssue | null
    closerYardName: string | null
    closerYardReason?: string | null
}

export function resolvePosition(
    location: Pick<RunMapLocation, 'latitude' | 'longitude'> | undefined | null,
): {position: LatLng | null; issue: LocationIssue | null} {
    if (!location || location.latitude == null || location.longitude == null) {
        return {position: null, issue: 'no_location'}
    }
    if (!isRealSite(location.latitude, location.longitude)) {
        return {position: null, issue: 'not_a_site'}
    }

    return {position: {lat: location.latitude, lng: location.longitude}, issue: null}
}

export function yardPosition(yard: RunMapYard | null | undefined): LatLng | null {
    return resolvePosition(yard).position
}

export function estimateLeg(from: LatLng, to: LatLng): LegTime {
    const miles = haversineMiles(from, to) * ROAD_FACTOR
    return {miles, minutes: (miles / ESTIMATE_MPH) * 60, method: 'estimate'}
}

/** Cache key with coordinates rounded to 5 decimal places. */
export function legKey(from: LatLng, to: LatLng): string {
    const roundCoordinate = (value: number) => value.toFixed(5)
    return `${roundCoordinate(from.lat)},${roundCoordinate(from.lng)}>${roundCoordinate(to.lat)},${roundCoordinate(to.lng)}`
}

export function jobRunsOnDay(schedule: DispatchJobSchedule | undefined, day: string): boolean {
    if (!schedule) {
        return false
    }

    return schedule.startDate <= day && (schedule.endDate == null || schedule.endDate >= day)
}

export function crewOnDay(allocations: JobAllocation[], day: string): RunMapCrewMember[] {
    const crew = new Map<string, RunMapCrewMember>()
    for (const allocation of allocations) {
        if (
            allocation.kind !== 'worker' ||
            !bookingCoversDay(allocation.days, day) ||
            allocation.isPlaceholder
        ) {
            continue
        }
        crew.set(allocation.resourceId, {id: allocation.resourceId, name: allocation.resourceName})
    }
    return [...crew.values()]
}

export function staffingOnDay(
    job: {requirements?: JobRequirement[]; allocations?: JobAllocation[]},
    day: string,
): Staffing {
    const allocations = job.allocations ?? []
    for (const requirement of job.requirements ?? []) {
        if (requirement.kind !== 'worker' || requirement.quantity <= 0) {
            continue
        }
        const covering = allocations.filter(
            (allocation) =>
                allocation.kind === 'worker' &&
                allocation.requirementId === requirement.id &&
                bookingCoversDay(allocation.days, day),
        ).length
        if (covering < requirement.quantity) {
            return 'needs_driver'
        }
    }
    return 'staffed'
}

type BoardJobLike = {
    id: string
    jobCode: string
    label: string
    customer: string
    customerId?: string
    dispatchYardId?: string | null
    schedule?: DispatchJobSchedule
    requirements?: JobRequirement[]
    allocations?: JobAllocation[]
    closerYardName?: string | null
}

/** Returns 0 on the later days of an overnight job. */
function startMinutesOnDay(schedule: DispatchJobSchedule | undefined, day: string): number | null {
    const start = jobTimeToMinutes(schedule?.startTime ?? null)
    const end = jobTimeToMinutes(schedule?.endTime ?? null)
    const overnightLaterDay =
        schedule != null && schedule.startDate < day && start != null && end != null && end < start
    return overnightLaterDay ? 0 : start
}

export function buildRunMapJobs(
    data: Pick<DispatchTimelineData, 'unassignedJobs' | 'assignments'>,
    day: string,
    extras: Record<string, RunMapJobExtras>,
): RunMapJob[] {
    const seen = new Set<string>()
    const boardJobs: BoardJobLike[] = [
        ...data.unassignedJobs,
        ...data.assignments.map((assignment) => ({
            ...assignment,
            id: assignment.sourceJobId ?? assignment.id,
        })),
    ]

    const jobs: RunMapJob[] = []
    for (const job of boardJobs) {
        if (seen.has(job.id) || !jobRunsOnDay(job.schedule, day)) {
            continue
        }
        seen.add(job.id)
        const extra = extras[job.id]
        const firstLocation = extra?.locations[0] ?? null
        const {position, issue} = resolvePosition(firstLocation)
        jobs.push({
            id: job.id,
            jobCode: job.jobCode,
            label: job.label,
            customer: job.customer,
            customerId: job.customerId ?? null,
            yardId: job.dispatchYardId ?? null,
            startMinutes: startMinutesOnDay(job.schedule, day),
            staffing: staffingOnDay(job, day),
            crew: crewOnDay(job.allocations ?? [], day),
            position,
            locationName: firstLocation?.name ?? null,
            locationIssue: issue,
            closerYardName: extra ? extra.closerYardName : (job.closerYardName ?? null),
            closerYardReason: extra?.closerYardReason ?? null,
        })
    }
    return jobs.sort(compareByStart)
}

export function jobsOnYardBoard(
    jobs: RunMapJob[],
    yardId: string | null,
    customerId: string | null = null,
): RunMapJob[] {
    return jobs.filter(
        (job) =>
            (!yardId || job.yardId == null || job.yardId === yardId) &&
            (!customerId || job.customerId === customerId),
    )
}

export function compareByStart(
    a: Pick<RunMapJob, 'startMinutes' | 'jobCode'>,
    b: Pick<RunMapJob, 'startMinutes' | 'jobCode'>,
): number {
    const aStart = a.startMinutes ?? Number.POSITIVE_INFINITY
    const bStart = b.startMinutes ?? Number.POSITIVE_INFINITY
    if (aStart !== bStart) {
        return aStart < bStart ? -1 : 1
    }

    return a.jobCode.localeCompare(b.jobCode)
}

export type RunMapDriver = RunMapCrewMember & {jobCount: number}

export function driversOnDay(jobs: RunMapJob[]): RunMapDriver[] {
    const drivers = new Map<string, RunMapDriver>()
    for (const job of jobs) {
        for (const member of job.crew) {
            const existing = drivers.get(member.id)
            drivers.set(member.id, {...member, jobCount: (existing?.jobCount ?? 0) + 1})
        }
    }
    return [...drivers.values()].sort((a, b) => a.name.localeCompare(b.name))
}

export type RunStop = {number: number; job: RunMapJob; position: LatLng}

export type RunLeg = {
    key: string
    from: LatLng
    to: LatLng
    fromLabel: string
    toLabel: string
    /** Null for the drive back to the yard. */
    toStop: RunStop | null
    /** Null for the drive out of the yard. */
    fromStop: RunStop | null
}

export type DriverRun = {
    driver: RunMapCrewMember
    yard: RunMapYard | null
    origin: LatLng | null
    stops: RunStop[]
    legs: RunLeg[]
    unmappedJobs: RunMapJob[]
}

export function buildDriverRun(
    jobs: RunMapJob[],
    driver: RunMapCrewMember,
    yard: RunMapYard | null,
): DriverRun {
    const driverJobs = jobs
        .filter((job) => job.crew.some((member) => member.id === driver.id))
        .sort(compareByStart)
    const stops: RunStop[] = []
    const unmappedJobs: RunMapJob[] = []
    for (const job of driverJobs) {
        if (job.position) {
            stops.push({number: stops.length + 1, job, position: job.position})
        } else {
            unmappedJobs.push(job)
        }
    }

    const origin = yardPosition(yard)
    const legs: RunLeg[] = []
    stops.forEach((stop, index) => {
        const fromStop = index === 0 ? null : (stops[index - 1] ?? null)
        const from = fromStop ? fromStop.position : origin
        if (!from) {
            return
        }

        legs.push({
            key: legKey(from, stop.position),
            from,
            to: stop.position,
            fromLabel: fromStop ? formatRunStopLabel(fromStop) : (yard?.name ?? 'Yard'),
            toLabel: formatRunStopLabel(stop),
            toStop: stop,
            fromStop,
        })
    })
    const lastStop = stops[stops.length - 1]
    if (origin && lastStop) {
        legs.push({
            key: legKey(lastStop.position, origin),
            from: lastStop.position,
            to: origin,
            fromLabel: formatRunStopLabel(lastStop),
            toLabel: yard?.name ?? 'Yard',
            toStop: null,
            fromStop: lastStop,
        })
    }

    return {driver, yard, origin, stops, legs, unmappedJobs}
}

function formatRunStopLabel(stop: RunStop): string {
    return `${stop.number}. ${stop.job.jobCode}`
}

export type EvaluatedLeg = RunLeg &
    LegTime & {
        arrivalMinutes: number | null
        /** Null unless arrival is after the destination job's start. */
        lateByMinutes: number | null
    }

export type RunTotals = {
    miles: number
    minutes: number
    method: DriveMethod | 'mixed' | null
}

export type EvaluatedRun = Omit<DriverRun, 'legs'> & {legs: EvaluatedLeg[]; totals: RunTotals}

/**
 * Compute arrival time, lateness and totals for each leg. The first stop is reached at its start
 * time and each stop takes {@link ON_SITE_MINUTES}.
 */
export function evaluateRun(run: DriverRun, knownTimes: Record<string, LegTime>): EvaluatedRun {
    const firstStart = run.origin == null ? (run.stops[0]?.job.startMinutes ?? null) : null
    let readyToLeave: number | null = firstStart == null ? null : firstStart + ON_SITE_MINUTES
    const legs: EvaluatedLeg[] = []
    const methods = new Set<DriveMethod>()

    for (const leg of run.legs) {
        const time = knownTimes[leg.key] ?? estimateLeg(leg.from, leg.to)
        methods.add(time.method)
        const start = leg.toStop?.job.startMinutes ?? null
        let arrivalMinutes: number | null
        let lateByMinutes: number | null = null

        if (leg.toStop == null) {
            arrivalMinutes = readyToLeave == null ? null : readyToLeave + time.minutes
            legs.push({...leg, ...time, arrivalMinutes, lateByMinutes})
            continue
        }
        if (leg.fromStop == null) {
            arrivalMinutes = start
        } else {
            const leave: number | null = readyToLeave
            arrivalMinutes = leave == null ? null : leave + time.minutes
            if (
                arrivalMinutes != null &&
                start != null &&
                Math.round(arrivalMinutes - start) >= 1
            ) {
                lateByMinutes = Math.round(arrivalMinutes - start)
            }
        }

        const onSiteFrom =
            start != null && arrivalMinutes != null
                ? Math.max(start, arrivalMinutes)
                : (start ?? arrivalMinutes)
        readyToLeave = onSiteFrom == null ? null : onSiteFrom + ON_SITE_MINUTES
        legs.push({...leg, ...time, arrivalMinutes, lateByMinutes})
    }

    const totals: RunTotals = {
        miles: legs.reduce((sum, leg) => sum + leg.miles, 0),
        minutes: legs.reduce((sum, leg) => sum + leg.minutes, 0),
        method: methods.size === 0 ? null : methods.size > 1 ? 'mixed' : ([...methods][0] ?? null),
    }
    return {...run, legs, totals}
}

export type RunMapWarning =
    | {kind: 'late_leg'; key: string; jobId: string; message: string}
    | {kind: 'closer_yard'; key: string; jobId: string; message: string}
    | {kind: 'no_location'; key: string; jobId: string; message: string}

export function lateLegMessage(leg: EvaluatedLeg, driverName: string): string {
    return `${driverName} cannot reach ${leg.toStop?.job.jobCode ?? leg.toLabel} by ${formatClockMinutes(
        leg.toStop?.job.startMinutes ?? 0,
    )}, about ${formatDuration(leg.lateByMinutes ?? 0)} late (${ON_SITE_ASSUMPTION_LABEL})`
}

export function collectWarnings(jobs: RunMapJob[], runs: EvaluatedRun[]): RunMapWarning[] {
    const warnings: RunMapWarning[] = []
    for (const run of runs) {
        for (const leg of run.legs) {
            if (leg.lateByMinutes != null && leg.toStop) {
                warnings.push({
                    kind: 'late_leg',
                    key: `late:${run.driver.id}:${leg.toStop.job.id}`,
                    jobId: leg.toStop.job.id,
                    message: lateLegMessage(leg, run.driver.name),
                })
            }
        }
    }
    for (const job of jobs) {
        if (job.closerYardName) {
            warnings.push({
                kind: 'closer_yard',
                key: `closer:${job.id}`,
                jobId: job.id,
                message: `${job.jobCode} is closer to ${job.closerYardName}${
                    job.closerYardReason ? `. ${job.closerYardReason}` : ''
                }`,
            })
        }
    }
    for (const job of jobs) {
        if (job.locationIssue) {
            const detail =
                job.locationIssue === 'not_a_site' ? 'invalid coordinates' : 'no coordinates'
            warnings.push({
                kind: 'no_location',
                key: `nolocation:${job.id}`,
                jobId: job.id,
                message: `${job.jobCode} has no usable location (${detail})`,
            })
        }
    }
    return warnings
}

/** Resolve the selected yard, then the driver's home yard, then the yard of their first job. */
export function startYardForDriver(
    driverId: string,
    jobs: RunMapJob[],
    yards: RunMapYard[],
    selectedYardId: string | null,
    homeYardIds: Record<string, string | null | undefined>,
): RunMapYard | null {
    const findYardById = (id: string | null | undefined) =>
        id == null ? null : (yards.find((yard) => yard.id === id) ?? null)
    if (selectedYardId) {
        return findYardById(selectedYardId)
    }

    const home = findYardById(homeYardIds[driverId])
    if (home) {
        return home
    }

    const firstJob = jobs
        .filter((job) => job.crew.some((member) => member.id === driverId))
        .sort(compareByStart)[0]
    return findYardById(firstJob?.yardId)
}

export function yardColorIndex(yards: Pick<RunMapYard, 'id'>[], yardId: string | null): number {
    if (yardId == null) {
        return -1
    }

    return yards.findIndex((yard) => yard.id === yardId)
}

export function formatClockMinutes(minutesAfterMidnight: number): string {
    const dayMinutes = ((Math.round(minutesAfterMidnight) % 1440) + 1440) % 1440
    const hours = String(Math.floor(dayMinutes / 60)).padStart(2, '0')
    const minutes = String(dayMinutes % 60).padStart(2, '0')
    return formatJobTime(`${hours}:${minutes}`)
}

export function formatDuration(minutes: number): string {
    const rounded = Math.max(0, Math.round(minutes))
    if (rounded < 60) {
        return `${rounded} min`
    }

    const hours = Math.floor(rounded / 60)
    const rest = rounded % 60
    return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')} min`
}

export function formatMiles(miles: number): string {
    return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`
}

export type SchematicProjection = (point: LatLng) => {x: number; y: number}

/** Equirectangular projection with longitude scaled by cos(mean latitude). */
export function fitProjection(
    points: LatLng[],
    width: number,
    height: number,
    padding: number,
): SchematicProjection {
    if (points.length === 0) {
        return () => ({x: width / 2, y: height / 2})
    }

    const lats = points.map((point) => point.lat)
    const lngs = points.map((point) => point.lng)
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    const minLng = Math.min(...lngs)
    const maxLng = Math.max(...lngs)
    const lngScale = Math.cos(toRadians((minLat + maxLat) / 2))
    const spanX = Math.max((maxLng - minLng) * lngScale, 1e-6)
    const spanY = Math.max(maxLat - minLat, 1e-6)
    const innerW = width - padding * 2
    const innerH = height - padding * 2
    const scale = Math.min(innerW / spanX, innerH / spanY)
    const offsetX = padding + (innerW - spanX * scale) / 2
    const offsetY = padding + (innerH - spanY * scale) / 2
    return (point) => ({
        x: offsetX + (point.lng - minLng) * lngScale * scale,
        y: offsetY + (maxLat - point.lat) * scale,
    })
}

/** Calendar dates from `start` to `end`, iterated in UTC. */
export function calendarDaysBetween(start: string, end: string, limit = 62): string[] {
    const [year = 1970, month = 1, dayOfMonth = 1] = start.split('-').map(Number)
    const days: string[] = []
    for (let offset = 0; offset < limit; offset++) {
        const day = new Date(Date.UTC(year, month - 1, dayOfMonth + offset))
            .toISOString()
            .slice(0, 10)
        if (day > end) {
            break
        }
        days.push(day)
    }
    return days
}

export function defaultMapDay(days: string[], today: string): string | null {
    if (days.includes(today)) {
        return today
    }

    return days[0] ?? null
}
